import type {
  ChatSessionReplayPart,
  ChatSessionTraceItem,
  ReplaySourceBlock,
} from '@/api/ai-chat/types';

import {
  formatToolInput,
  isRecord,
  readClarificationOptions,
  readProposalChanges,
  readProposalOptions,
  readString,
  toolResultStatus,
} from './parse';
import { acceptanceFields, readProposalWireFlags } from './proposalAcceptance';

export { readProposalChanges } from './parse';

export const traceFromBlocks = (
  blocks: ReplaySourceBlock[]
): ChatSessionTraceItem[] => {
  const trace: ChatSessionTraceItem[] = [];
  blocks.forEach((block) => {
    if (block.type === 'reasoning') {
      if (block.text) trace.push({ kind: 'text', text: block.text });
      return;
    }
    if (block.type !== 'tool_use' && block.type !== 'tool_result') return;
    const extra = isRecord(block.extra) ? block.extra : {};
    const id =
      (block.type === 'tool_result'
        ? readString(extra.tool_use_id)
        : undefined) ||
      readString(extra.id) ||
      `tool-${block.index}`;
    const name =
      readString(extra.name) || readString(extra.tool_name) || '工具';
    if (block.type === 'tool_use') {
      const input = formatToolInput(extra.arguments) || block.text || undefined;
      trace.push({
        kind: 'tool',
        id,
        name,
        ...(input ? { input } : {}),
        status: 'running',
      });
      return;
    }
    const output = readString(extra.content) || block.text || undefined;
    const status = toolResultStatus(output, extra.is_error === true);
    const match = [...trace]
      .reverse()
      .find((item) => item.kind === 'tool' && item.id === id);
    if (match && match.kind === 'tool') {
      match.output = output;
      match.status = status;
      return;
    }
    trace.push({ kind: 'tool', id, name, output, status });
  });
  return trace;
};

/** 历史 clarification 块 → clarifications。带 options 的题由 findPendingClarifications 收成输入区 choices。 */
export const clarificationsFromBlocks = (blocks: ReplaySourceBlock[]) =>
  blocks.flatMap((block) => {
    if (block.type !== 'clarification') return [];
    const extra = isRecord(block.extra) ? block.extra : {};
    const message = readString(extra.message) || block.text || '';
    const questions = Array.isArray(extra.questions)
      ? extra.questions.filter(
          (item): item is string => typeof item === 'string'
        )
      : [];
    const options = readClarificationOptions(extra.options);
    if (!message && !questions.length && !options.length) return [];
    return [{ message, questions, options }];
  });

/** 同一轮多条提案时后一条覆盖前一条，和 SSE patch 一致。options 在没有澄清题时收成输入区 choices。 */
export const proposalFromBlocks = (blocks: ReplaySourceBlock[]) => {
  const block = [...blocks].reverse().find((item) => item.type === 'proposal');
  if (!block) return undefined;
  const extra = isRecord(block.extra) ? block.extra : {};
  const raw = isRecord(extra.proposal) ? extra.proposal : null;
  if (!raw) return undefined;
  const fields = acceptanceFields(
    readProposalWireFlags({ ...extra, proposal: raw })
  );
  return {
    block,
    proposal: {
      proposalId: readString(raw.proposal_id) || '',
      status: fields.status,
      changes: readProposalChanges(raw.changes),
      options: readProposalOptions(extra.options),
      ...(fields.locked ? { locked: true } : {}),
      ...(fields.selectedAction
        ? { selectedAction: fields.selectedAction }
        : {}),
    },
  };
};

const isThinkBlock = (block: ReplaySourceBlock) =>
  block.type === 'reasoning' ||
  block.type === 'tool_use' ||
  block.type === 'tool_result';

const thinkPart = (
  group: ReplaySourceBlock[]
): ChatSessionReplayPart | undefined => {
  const trace = traceFromBlocks(group);
  const progress = group
    .filter((block) => block.type === 'reasoning' && block.text)
    .map((block) => block.text)
    .join('\n');
  if (trace.some((item) => item.kind === 'tool')) {
    return { kind: 'think', progress, trace };
  }
  if (!progress) return undefined;
  return { kind: 'think', progress };
};

/**
 * 按 index 排出可见块。相邻正文拼成一段，相邻思考/工具收成一张思考卡。
 * 提案留在它自己的 index 上。同一轮多条提案只渲染最后一条。
 * cancel 单独成段，不并进正文。attachment 不占位置，文件另画在本轮末尾。
 * 历史回放和 SSE 共用这一份。
 */
export const replayPartsFromBlocks = (
  blocks: ReplaySourceBlock[]
): ChatSessionReplayPart[] => {
  const ordered = [...blocks].sort((left, right) => left.index - right.index);
  const picked = proposalFromBlocks(ordered);
  const parts: ChatSessionReplayPart[] = [];
  let texts: string[] = [];
  let think: ReplaySourceBlock[] = [];

  const flushTexts = () => {
    if (!texts.length) return;
    parts.push({ kind: 'text', text: texts.join('\n\n') });
    texts = [];
  };
  const flushThink = () => {
    if (!think.length) return;
    const part = thinkPart(think);
    think = [];
    if (part) parts.push(part);
  };

  ordered.forEach((block) => {
    if (block.type === 'attachment') return;
    if (isThinkBlock(block)) {
      flushTexts();
      think.push(block);
      return;
    }
    flushThink();
    if (block.type === 'text') {
      if (block.text) texts.push(block.text);
      return;
    }
    if (block.type === 'proposal') {
      if (!picked || block !== picked.block) return;
      flushTexts();
      parts.push({ kind: 'proposal' });
      return;
    }
    if (block.type === 'cancel') {
      flushTexts();
      parts.push({
        kind: 'cancel',
        text: block.text?.trim() || '用户已取消',
      });
      return;
    }
    flushTexts();
    if (block.type !== 'clarification') return;
    const [item] = clarificationsFromBlocks([block]);
    if (!item) return;
    // 有选项的澄清按钮在输入区，气泡只留题干。
    const hasOptions = item.options.length > 0;
    if (hasOptions) {
      if (item.message) {
        parts.push({ kind: 'clarification', text: item.message });
      }
      return;
    }
    if (!item.message && !item.questions.length) return;
    parts.push({
      kind: 'clarification',
      text: item.message || '需要确认',
      ...(item.questions.length ? { questions: item.questions } : {}),
    });
  });
  flushThink();
  flushTexts();
  return parts;
};
